// Notification link + copy helpers — Phase 3 Module 9.
//
// Given a row from rpc_list_my_notifications(), derive:
//   * a deep-link path based on the caller's role
//   * a short, parent-friendly label describing the event
//   * an icon identifier (used by the bell dropdown)
//
// Why parse idempotency_key?  Because the log table stores the source
// entity id inside the idempotency key (see /app/backend/notifications.py
// where each sender builds its own key). This keeps the log schema flat
// and avoids a dedicated `source_id` column that would need per-kind
// migrations. The parsing below mirrors those key shapes 1:1.

import { ROLES } from "@/lib/roles";

/** Parse "prefix:a:b:c" → ["a","b","c"]. Tolerant to missing parts. */
function parts(key) {
  if (!key || typeof key !== "string") return [];
  const idx = key.indexOf(":");
  if (idx < 0) return [];
  return key.slice(idx + 1).split(":");
}

/**
 * @param {object} row  Notification log row as returned by rpc_list_my_notifications.
 * @param {string} role Caller role (ROLES.PARENT | ROLES.ADMIN | ...).
 * @returns {{icon:string,label:string,path:string|null,statusTone:string,isFailure:boolean}}
 */
export function describeNotification(row, role) {
  const md = row?.metadata || {};
  const key = row?.idempotency_key || "";
  const kind = row?.kind;
  const isFailure = row?.status === "failed";

  // Base tone for the status pill.
  const statusTone =
    row.status === "sent"    ? "border-primary/30 bg-primary/10 text-primary-hover" :
    row.status === "failed"  ? "border-[#C6851E]/40 bg-[#C6851E]/10 text-[#C6851E]" :
    row.status === "skipped" ? "border-neutral-200 bg-neutral-100 text-neutral-500" :
    /* default */              "border-neutral-200 bg-neutral-100 text-neutral-500";

  const isParent = role === ROLES.PARENT;
  const isAdmin  = role === ROLES.ADMIN;

  // Native delivery rows are transport records, just like Web Push rows.
  // Parents and teachers see the canonical event; administrators retain a
  // clear transport label in their delivery log.
  if (isNativeMessage(row)) {
    const description = describeNotification(
      { ...row, kind: "new_message_push" },
      role,
    );
    return {
      ...description,
      label: isAdmin ? "New message (native push)" : description.label,
    };
  }
  if (isNativePhotoReport(row)) {
    const description = describeNotification(
      { ...row, kind: "photo_reported" },
      role,
    );
    return {
      ...description,
      label: isAdmin ? "Photo reported (native push)" : description.label,
    };
  }

  switch (kind) {
    case "daily_report": {
      const [reportId, reportDate] = parts(key);
      const path =
        isParent && reportDate ? `/parent/activity/${reportDate}` :
        isParent                ? `/parent/activity` :
        isAdmin                 ? `/admin/daily-reports` : null;
      return { icon: "Sun", label: "Daily report", path, statusTone, isFailure };
    }

    case "incident": {
      const [incidentId] = parts(key);
      const basePath = isParent ? "/parent/incidents" : "/admin/incidents";
      const path = incidentId ? `${basePath}/${incidentId}` : basePath;
      return { icon: "FirstAid", label: "Incident report", path, statusTone, isFailure };
    }

    case "invoice_issued": {
      const [invoiceId] = parts(key);
      const path = isParent
        ? `/parent/billing`
        : `/admin/billing${invoiceId ? `?invoice=${invoiceId}` : ""}`;
      return { icon: "Receipt", label: "New invoice", path, statusTone, isFailure };
    }

    case "autopay_failure": {
      const [invoiceId] = parts(key);
      const path = isParent
        ? `/parent/billing`
        : `/admin/billing${invoiceId ? `?invoice=${invoiceId}` : ""}`;
      return { icon: "WarningCircle", label: "Autopay failure", path, statusTone, isFailure: true };
    }

    case "event_rsvp_confirmed":
    case "event_reminder": {
      const path = isParent ? `/parent/events` : `/admin/events`;
      return {
        icon: "CalendarCheck",
        label: kind === "event_rsvp_confirmed" ? "RSVP confirmed" : "Event reminder",
        path,
        statusTone,
        isFailure,
      };
    }

    case "weekly_photo_recap": {
      const path = isParent ? `/parent/activity` : `/admin/daily-reports`;
      return { icon: "Camera", label: "Weekly photo recap", path, statusTone, isFailure };
    }

    case "new_message":
    case "new_message_push": {
      const [conversationId] = parts(key);
      const isTeacher = role === ROLES.TEACHER;
      let path;
      if (conversationId && isParent)       path = `/parent/messages/${conversationId}`;
      else if (conversationId && isTeacher) path = `/teacher/messages/${conversationId}`;
      else if (conversationId && isAdmin)   path = `/admin/messaging/${conversationId}`;
      else if (isParent)                    path = `/parent/messages`;
      else if (isTeacher)                   path = `/teacher/messages`;
      else if (isAdmin)                     path = `/admin/messaging`;
      else                                  path = null;

      // Mention takes visual priority — admins/teachers/parents all
      // want to see "X mentioned you" jump out from regular message
      // noise. Metadata flag is authoritative; the idempotency_key
      // prefix is a second defensive signal.
      const isMention =
        md.mention === true ||
        (typeof key === "string" && key.startsWith("new_message_mention"));
      const senderName = md.sender_name || "";
      return {
        icon: isMention
          ? "At"
          : kind === "new_message_push" ? "BellRinging" : "ChatCircleText",
        label: isMention
          ? (senderName ? `${senderName} mentioned you` : "You were mentioned")
          : kind === "new_message_push" ? "New message (push)" : "New message",
        path,
        statusTone,
        isFailure,
        detail: isAdmin
          ? (row.subject || "—")
          : (senderName ? `From ${senderName}` : "Open the conversation to read it."),
      };
    }

    case "enrollment_inquiry": {
      // Idempotency key shape: "enrollment_inquiry:{inquiry_id}:{admin_profile_id}"
      const [inquiryId] = parts(key);
      const path = isAdmin
        ? inquiryId
          ? `/admin/inquiries/${inquiryId}`
          : "/admin/inquiries"
        : null;
      return {
        icon: "Sparkle",
        label: "New enrollment inquiry",
        path,
        statusTone,
        isFailure,
      };
    }

    case "photo_reported": {
      // Idempotency key shape: "photo_reported:{photo_id}:{admin_profile_id}"
      // Deep-link always goes to the moderation queue — per-photo URLs
      // don't exist yet (Slice C consideration). Admin-only surface.
      const path = isAdmin ? "/admin/photos/moderation" : null;
      return {
        icon: "Flag",
        label: md.reporter_name || md.child_name
          ? `Photo reported${md.child_name ? ` — ${md.child_name}` : ""}`
          : "Photo reported",
        path,
        statusTone,
        isFailure,
      };
    }

    default:
      return { icon: "Bell", label: kind || "Notification", path: null, statusTone, isFailure, detail: row.subject || "—" };
  }
}

/**
 * Turn transport-level message delivery rows into one user-facing event.
 * Admins retain the raw delivery log; parents and teachers should only see
 * the message event, never separate email/push attempts or their failures.
 */
export function projectNotificationRows(rows, { role } = {}) {
  const isTransportVisible = role === ROLES.ADMIN;
  const source = Array.isArray(rows) ? rows : [];
  if (isTransportVisible) return source;

  const merged = new Map();
  for (const row of source) {
    const isMessage = row?.kind === "new_message"
      || row?.kind === "new_message_push"
      || isNativeMessage(row);
    const key = isMessage
      ? `message:${normalizeMessageKey(row.idempotency_key) || row.id}`
      : `row:${row.id}`;
    const previous = merged.get(key);
    if (!previous) {
      merged.set(key, { ...row });
      continue;
    }

    const preferred = previous.kind === "new_message" || row.kind !== "new_message"
      ? previous
      : row;
    merged.set(key, {
      ...preferred,
      metadata: { ...(previous.metadata || {}), ...(row.metadata || {}) },
      is_unread: Boolean(previous.is_unread || row.is_unread),
      created_at: new Date(previous.created_at || 0) >= new Date(row.created_at || 0)
        ? previous.created_at
        : row.created_at,
      // A successful transport is enough to represent the event. Failed or
      // skipped-only rows are filtered below so they don't leak into the UI.
      status: previous.status === "sent" || row.status === "sent" ? "sent" : preferred.status,
    });
  }

  return [...merged.values()]
    .filter((row) => row.status !== "failed" && row.status !== "skipped")
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
}

function normalizeMessageKey(key) {
  if (!key || typeof key !== "string") return "";
  return key
    .replace(/^new_message_mention_native_push:/, "new_message_mention:")
    .replace(/^new_message_mention_push:/, "new_message_mention:")
    .replace(/^new_message_native_push:/, "new_message:")
    .replace(/^new_message_push:/, "new_message:");
}

function isNativeMessage(row) {
  const key = row?.idempotency_key;
  return row?.kind === "native_push"
    && typeof key === "string"
    && (
      key.startsWith("new_message_native_push:")
      || key.startsWith("new_message_mention_native_push:")
    );
}

function isNativePhotoReport(row) {
  const key = row?.idempotency_key;
  return row?.kind === "native_push"
    && typeof key === "string"
    && key.startsWith("photo_reported_native_push:");
}

/** Short humanised relative time ("just now", "4m", "2h", "3d"). */
export function relTime(iso) {
  if (!iso) return "";
  const now = Date.now();
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Math.floor((now - then) / 1000));
  if (diff < 45) return "just now";
  if (diff < 60 * 60)      return `${Math.floor(diff / 60)}m`;
  if (diff < 60 * 60 * 24) return `${Math.floor(diff / 3600)}h`;
  const days = Math.floor(diff / (60 * 60 * 24));
  if (days < 7)  return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  return new Date(iso).toLocaleDateString();
}

// Grouping buckets for the "grouped by type" render.
export const NOTIFICATION_GROUPS = [
  { key: "messages",   label: "Messages",      kinds: ["new_message", "new_message_push"] },
  { key: "inquiries",  label: "Inquiries",     kinds: ["enrollment_inquiry"] },
  { key: "moderation", label: "Moderation",    kinds: ["photo_reported"] },
  { key: "billing",    label: "Billing",       kinds: ["invoice_issued", "autopay_failure"] },
  { key: "events",     label: "Events",        kinds: ["event_rsvp_confirmed", "event_reminder"] },
  { key: "reports",    label: "Daily reports", kinds: ["daily_report", "weekly_photo_recap"] },
  { key: "incidents",  label: "Incidents",     kinds: ["incident"] },
  { key: "other",      label: "Other",         kinds: [] }, // catch-all
];

/** Icon names for group filter chips — mirrors the icons we already use
 *  per-row in the bell. The page renders them via the same ICON_MAP. */
export const GROUP_META = {
  messages:   { icon: "ChatCircleText" },
  inquiries:  { icon: "Flag" },
  moderation: { icon: "Flag" },
  billing:    { icon: "Receipt" },
  events:     { icon: "CalendarCheck" },
  reports:    { icon: "Sun" },
  incidents:  { icon: "FirstAid" },
  other:      { icon: "Bell" },
};

export function groupNotifications(rows) {
  const buckets = Object.fromEntries(NOTIFICATION_GROUPS.map((g) => [g.key, []]));
  for (const row of rows || []) {
    const g = isNativeMessage(row)
      ? NOTIFICATION_GROUPS.find((x) => x.key === "messages")
      : isNativePhotoReport(row)
        ? NOTIFICATION_GROUPS.find((x) => x.key === "moderation")
        : NOTIFICATION_GROUPS.find((x) => x.kinds.includes(row.kind));
    (buckets[g ? g.key : "other"] || buckets.other).push(row);
  }
  return buckets;
}
